// copied and edited from packages/ui/src/components/FlavorFinder/components/ViewSwitch/FlavorFinderViewSwitch.tsx
import type { FC, ReactNode } from "react";
import { useEffect } from "react";

import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { FlavorFinderConsent } from "../Consent/FlavorFinderConsent";
import { FlavorFinderIntro } from "../Intro/FlavorFinderIntro";
import { FlavorFinderOptionalQuestion } from "../OptionalQuestion/FlavorFinderOptionalQuestion";
import { useFlavorFinderContext } from "../Provider/FlavorFinderContextProvider";
import { FlavorFinderQuestion } from "../Question/FlavorFinderQuestion";
import { FlavorFinderResults } from "../Results/FlavorFinderResults";
import { FlavorFinderStepper } from "../Stepper";

import styles from "./FlavorFinderViewSwitch.module.scss";

export const FlavorFinderViewSwitch: FC = () => {
  const { flavorFinderData, currentQuestion, currentView, currentQuestionIndex, quizResult } = useFlavorFinderContext();
  const {
    nextCtaLabel,
    backCtaLabel,
    continueCtaLabel,
    questionNumberIndicator,
    selectionIndicator,
    totalNumberOfQuestions,
    finalQuestion,
    skipCtaLabel,
    hideNextButton,
    optionalQuestionErrorText,
  } = flavorFinderData;

  const hasOptionalStep = Boolean(finalQuestion);
  const totalSteps = totalNumberOfQuestions + (hasOptionalStep ? 3 : 2);
  const shouldShowStepper =
    currentView === "intro" ||
    currentView === "question" ||
    currentView === "optional" ||
    currentView === "consent" ||
    currentView === "results";

  const currentStep = (() => {
    if (currentView === "intro") {
      return 0;
    }

    if (currentView === "question") {
      return currentQuestionIndex + 1;
    }

    if (currentView === "optional") {
      return totalSteps - 2;
    }

    if (currentView === "consent") {
      return totalSteps - 1;
    }

    return totalSteps;
  })();

  useEffect(() => {
    if (currentView === "results") return;

    const stepIncrements: Record<string, number> = {
      consent: 2,
      results: 3,
    };

    const flavor_finder_step_number = currentQuestionIndex + (stepIncrements[currentView] || 1);

    dispatchTrackingEvent(TrackingEvents.TrackingFlavorFinderProgress, {
      flavor_finder_step_name:
        currentView === "question" ? currentQuestion?.question?.title || currentView : currentView,
      flavor_finder_step_number,
    });
  }, [currentView, currentQuestion, currentQuestionIndex]);

  let content: ReactNode = null;

  switch (currentView) {
    case "intro":
      content = <FlavorFinderIntro />;
      break;
    case "question":
      content = currentQuestion && (
        <FlavorFinderQuestion
          question={currentQuestion}
          backCtaLabel={backCtaLabel}
          nextCtaLabel={nextCtaLabel}
          questionNumberIndicator={questionNumberIndicator}
          selectionIndicator={selectionIndicator}
          totalNumberOfQuestions={totalNumberOfQuestions}
          hideNextButton={hideNextButton}
        />
      );
      break;
    case "optional":
      content = finalQuestion ? (
        <FlavorFinderOptionalQuestion
          question={finalQuestion}
          skipCtaLabel={skipCtaLabel}
          backCtaLabel={backCtaLabel}
          continueCtaLabel={continueCtaLabel}
          selectionIndicator={selectionIndicator}
          hideNextButton={hideNextButton}
          errorText={optionalQuestionErrorText || "Please select an answer"}
        />
      ) : null;
      break;
    case "consent":
      content = <FlavorFinderConsent />;
      break;
    case "results":
      content = <FlavorFinderResults results={quizResult} />;
      break;
  }

  return (
    <>
      {shouldShowStepper && <FlavorFinderStepper currentStep={currentStep} totalSteps={totalSteps} />}
      <div className={styles.container}>{content}</div>
    </>
  );
};
